import React, { useState, useEffect, useRef, useMemo } from "react";
import { components } from "react-select";

import {
  Button,
  FlexDiv,
  Label,
  SVGSprite,
  CountryDropdown,
  Input,
  Select,
  Image,
} from "@/components/Atoms";
import { formatOptionLabel } from "@/components/Molecules/FormatOptionLabel";
import Loader from "@/components/Molecules/Loader";
import { Tags } from "@/components/Organisms/FormInputs/TagGroup/style";

import { Typography } from "@/foundation/Typography/V2";
import theme from "@/foundation/Theme";

import { useAppSelector } from "@/hooks";

import {
  ArrowDown,
  BodyWrap,
  BoldText,
  CountryPlaceholder,
  DropDown,
  ErrorDiv,
  FormBox,
  InputGroup,
  InputWrap,
  NumberPlaceholder,
  PhoneIcon,
  PromoCode,
  PromoCodeApplyButton,
  SelectGroup,
  StrikeThroughText,
  SubHeading,
} from "./style";
import { PhoneFlag } from "@/components/Organisms/FormInputsV2/PhoneInput/style";

const FormInputs = ({
  data,
  isLoading,

  //
  handleChange = () => {},
  handleSelectChange = () => {},
  handleBlur = () => {},
  onSubmitCallback = () => {},
  //
  showLabels = true,
  //
  showNameField,
  namePlaceHolder = "Enter your name",
  name,
  nameError,
  //
  showCVIDField,
  cvidPlaceholder = "Enter Club Vistara ID",
  cvid,
  cvidError,
  //
  showPhoneField,
  countryCode,
  mobilePlaceholder = "Your phone number",
  mobile,
  mobileError,
  //
  showEmailField,
  emailPlaceholder = "Your email address",
  email,
  emailError,
  //
  showCompanyNameField,
  companyNamePlaceholder = "Company Name",
  companyName,
  companyNameError,
  //
  showGoalField,
  goalOptions = [],
  goalPlaceholder = "Select a health goal",
  goal,
  goalError,
  goalValue,
  //
  showSelectPackage,
  //
  showPromoCodeField,
  promoCodePlaceholder = "Enter Promo Code",
  promoCode,
  promoCodeError,
  promoCodeButtonText,
  isPromoCodeChecking,
  isPromoCodeApplied,
  promoCodeData,
  applyPromoCode,
  //
  showReferralField,
  referralCodePlaceholder = "Enter Referral Code",
  referralCode,
  //
  //
  showSecondaryCTA,
  secondaryCTA,
  classInfo,
  needMoreInfo,
  //
  forwardedRef, //TODO: Confirm Use of This
  _services,
  serviceActive,
  setServiceActive,
  primaryCTAData,
  removeAppliedPromoCode,
}: any) => {
  const [services, setServices] = useState(_services);

  const currentOptions =
    services?.length > 0 ? services?.[serviceActive || 0]?.slugs : {};

  const [options, setOptions] = useState(currentOptions);
  const isStaticDropdown = !(data?.forms?.services?.length > 0);

  useEffect(() => {
    if (services?.length > 0) {
      handleSelectChange("service", services?.[serviceActive]?.value);
    }
  }, []);

  useEffect(() => {
    setOptions(currentOptions);

    const changedSlug =
      currentOptions?.length > 0
        ? currentOptions?.find((option) => option.value === goalValue)
        : "";

    handleSelectChange("goal", changedSlug?.slug);
  }, [serviceActive]);

  useEffect(() => {
    if (goalValue) {
      const filteredService = _services?.filter((service) => {
        const slugs = service?.slugs?.map((slug) => slug?.value);
        return slugs.includes(goalValue);
      });
      if (JSON.stringify(services) !== JSON.stringify(filteredService)) {
        const selectedService = services?.[serviceActive]?.value;
        const index = filteredService?.findIndex(
          (service) => service?.value === selectedService
        );
        setServiceActive(index);
        setServices(filteredService);
      }
    }
  }, [goalValue]);

  const wrapperRef = useRef(null);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowCountryDropDown(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }
  useOutsideAlerter(wrapperRef);

  const { countryCodeData: countryCodes } = useAppSelector(
    (state) => state.dashboard
  );

  const [showCountryDropDown, setShowCountryDropDown] = useState(false);

  const NameField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            variant="span"
            fontFamily="var(--font-700)"
          >
            NAME
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={namePlaceHolder}
          name="name"
          value={name}
          onChange={(name) => handleChange(name)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-user-face-2"
          iconFill={theme.colorsV2.gray.shade_700}
          iconDimension="16px"
          inputFontSize="16px"
          variant="v2"
        />
        {nameError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              fill={theme.colorsV2.shyft.error}
              icon="icon-info-circle-2"
              height="16px"
              width="16px"
              mr="8px"
            />
            <Typography
              variant="span"
              color={theme.colorsV2.shyft.error}
              fontFamily="var(--font-700)"
            >
              {nameError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const CVIDField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            variant="span"
            fontFamily="var(--font-700)"
          >
            CLUB VISTARA ID
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={cvidPlaceholder}
          name="cvid"
          value={cvid}
          onChange={(cvid) => handleChange(cvid)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-vistara-id"
          iconFill={theme.colorsV2.gray.shade_700}
          inputFontSize="16px"
          variant="v2"
        />
        {cvidError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              fill={theme.colorsV2.shyft.error}
              icon="icon-info-circle-2"
              height="16px"
              width="16px"
              mr="8px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {cvidError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const PhoneField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            variant="span"
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
          >
            PHONE NUMBER
          </Typography>
        </Label>
      )}

      <InputGroup showLabels={showLabels}>
        <InputWrap>
          <CountryPlaceholder showCountryDropDown={showCountryDropDown}>
            <NumberPlaceholder
              onClick={() => setShowCountryDropDown(!showCountryDropDown)}
              position="relative"
            >
              {countryCode === "+91" ||
              countryCode === "+971" ||
              countryCode === "+974" ||
              countryCode === "+1" ||
              countryCode === "+65" ||
              countryCode === "+44" ? (
                <PhoneFlag height={"16px"} width={"24px"}>
                  <Image
                    maxWidth={"24px"}
                    maxHeight={"16px"}
                    imageName={`flags/${
                      countryCode === "+91"
                        ? "india.png"
                        : countryCode === "+971"
                        ? "uae.png"
                        : countryCode === "+974"
                        ? "qatar.png"
                        : countryCode === "+1"
                        ? "usa.png"
                        : countryCode === "+65"
                        ? "singapore.png"
                        : countryCode === "+44" && "uk.png"
                    }`}
                    alt="flag"
                  />
                </PhoneFlag>
              ) : (
                <PhoneIcon
                  icon="icon-phone-2"
                  fill={theme.colorsV2.gray.shade_700}
                  height={"16px"}
                  width={"16px"}
                />
              )}
              <Typography
                variant="label2"
                fontSize="16px"
                // fontFamily="var(--font-500)"
                color={theme.colorsV2.gray.shade_700}
                width={"100%"}
                textAlign={"center"}
              >
                {countryCode}
              </Typography>
              <ArrowDown
                icon={"icon-caret-down"}
                height="8px"
                width="8px"
                fill={theme.colorsV2.gray.shade_700}
              />
            </NumberPlaceholder>
            <DropDown
              showCountryDropDown={showCountryDropDown}
              ref={wrapperRef}
            >
              <CountryDropdown
                menuIsOpen={showCountryDropDown}
                options={countryCodes?.list}
                formatOptionLabel={formatOptionLabel}
                wrapperClassName="shyftList"
                dropDownHeight="180px"
                placeholder="Search"
                defaultValue={countryCode}
                onChange={(countryCode) => {
                  handleSelectChange("countryCode", countryCode.code);
                  setShowCountryDropDown(false);
                }}
                closeMenuOnSelect
                variant={"v2"}
              />
            </DropDown>
          </CountryPlaceholder>
          <Input
            type="text"
            placeholder={mobilePlaceholder}
            inputPadding={"3px 16px 3px 100px"}
            pattern="[0-9]*"
            inputMode="decimal"
            name="mobile"
            value={mobile}
            onChange={(mobile) => handleChange(mobile)}
            onBlur={handleBlur}
            autoComplete="off"
            ref={forwardedRef}
            variant="v2"
            required
          />
        </InputWrap>
        {mobileError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              fill={theme.colorsV2.shyft.error}
              icon="icon-info-circle-2"
              height="16px"
              width="16px"
              mr="8px"
            />
            <Typography
              variant="span"
              color={theme.colorsV2.shyft.error}
              fontFamily="var(--font-700)"
            >
              {mobileError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const EmailField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            EMAIL ADDRESS
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="email"
          placeholder={emailPlaceholder}
          name="email"
          value={email}
          onChange={(email) => handleChange(email)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-envelope"
          iconFill={theme.colorsV2.gray.shade_700}
          inputFontSize="16px"
          icon="icon-EnvelopeSimple"
          variant="v2"
        />
        {emailError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              fill={theme.colorsV2.shyft.error}
              icon="icon-info-circle-2"
              height="16px"
              width="16px"
              mr="8px"
            />
            <Typography
              variant="span"
              color={theme.colorsV2.shyft.error}
              fontFamily="var(--font-700)"
            >
              {emailError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const CompanyNameField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="companyName">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            Company Name
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={companyNamePlaceholder}
          name="companyName"
          value={companyName}
          onChange={(companyName) => handleChange(companyName)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-briefcase"
          iconFill={theme.colorsV2.gray.shade_700}
          iconDimension="16px"
          inputFontSize="16px"
          variant="v2"
        />
        {companyNameError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              fill={theme.colorsV2.shyft.error}
              icon="icon-info-circle-2"
              height="16px"
              width="16px"
              mr="8px"
            />
            <Typography
              variant="span"
              color={theme.colorsV2.shyft.error}
              fontFamily="var(--font-700)"
            >
              {companyNameError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const GoalField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            YOUR GOAL
          </Typography>
        </Label>
      )}
      <SelectGroup>
        <Select
          options={goalOptions || options}
          dropDownHeight="150px"
          placeholder={goalPlaceholder}
          name="goal"
          value={
            goal
              ? isStaticDropdown
                ? goalOptions?.find((option) => option.value === goal)
                : options?.find((option) => option.slug === goal)
              : ""
          }
          onChange={(goal) => {
            if (isStaticDropdown) {
              handleSelectChange("goal", goal.value);
            } else {
              handleSelectChange("goal", goal.slug);
              handleSelectChange("goalValue", goal.value);
            }
          }}
          // components={{ DropdownIndicator }}
          containerPadding="2px 16px"
          dropdownPos="bottom"
          borderRadius="10px"
          singleValueColor={theme.colors.gray_90}
          isSearchable={false}
          onBlur={handleBlur}
          variant="v2"
        />
        {goalError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              fill={theme.colorsV2.shyft.error}
              icon="icon-info-circle-2"
              height="16px"
              width="16px"
              mr="8px"
            />
            <Typography
              variant="span"
              color={theme.colorsV2.shyft.error}
              fontFamily="var(--font-700)"
            >
              {goalError}
            </Typography>
          </ErrorDiv>
        )}
      </SelectGroup>
    </FlexDiv>
  );

  const ServiceField = showSelectPackage && services?.length > 0 && (
    <FlexDiv flexDirection={"column"}>
      <Label htmlFor="service">
        <Typography
          color={theme.colorsV2.gray.shade_400}
          variant="paragraph3"
          mb={"8px"}
        >
          What service are you looking for?
        </Typography>
      </Label>
      <InputGroup showLabels={true} hasNoError>
        <FlexDiv>
          {services?.map((item, index) => (
            <Tags
              onClick={
                serviceActive === index
                  ? () => {}
                  : () => {
                      handleSelectChange("service", item.value);
                      setServiceActive(index);
                    }
              }
              variant="label3"
              key={index}
              color={
                serviceActive === index
                  ? theme.colorsV2.shyft.purple_text
                  : theme.colorsV2.gray.shade_500
              }
              isSelected={serviceActive === index}
            >
              {item.label}
            </Tags>
          ))}
        </FlexDiv>
      </InputGroup>
    </FlexDiv>
  );

  const PromoCodeField = (
    <FlexDiv flexDirection="column">
      <PromoCode showLabels={showLabels}>
        <Input
          type="text"
          placeholder={promoCodePlaceholder}
          name="promoCode"
          value={promoCode?.toUpperCase()}
          onChange={(e) => handleChange(e)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          inputColor={
            isPromoCodeApplied ? theme.colors.link2Color : theme.colors.gray_90
          }
          icon={isPromoCodeApplied ? "icon-success-check" : "icon-discount"}
          iconFill={
            isPromoCodeApplied ? theme.colors.link2Color : theme.colors.quote
          }
          variant="v2"
          inputFontSize="16px"
          disabled={isPromoCodeApplied}
        >
          <PromoCodeApplyButton
            variant="link"
            fontSize="13px"
            TextColor={
              isPromoCodeApplied || promoCode?.length > 3
                ? theme.colors.link2Color
                : theme.colors.quote
            }
            onClick={(e) => {
              e.preventDefault();
              if (isPromoCodeApplied) {
                removeAppliedPromoCode();
              } else {
                applyPromoCode();
              }
            }}
            fontFamily="var(--font-700)"
          >
            {promoCodeButtonText}
            {isPromoCodeChecking && (
              <>
                &nbsp; <Loader />
              </>
            )}
          </PromoCodeApplyButton>
        </Input>
        {promoCodeError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
              flex={1}
            >
              {promoCodeError}
            </Typography>
          </ErrorDiv>
        )}
      </PromoCode>
    </FlexDiv>
  );

  const ReferralField = (
    <FlexDiv flexDirection="column">
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={referralCodePlaceholder}
          name="referralCode"
          value={referralCode}
          onChange={(e) => handleChange(e)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          //   inputColor={theme.colors.gray_90}
          icon="icon-discount"
          variant="v2"
          inputFontSize="16px"
          iconFill={theme.colorsV2.gray.shade_700}
        />
      </InputGroup>
    </FlexDiv>
  );

  const PrimaryCTA = (
    <FlexDiv
      justifyContent="flext-start"
      // mt={isBottomForm ? (isMobile ? "8px" : "16px") : "8px"}
      mt="8px"
    >
      <Button
        onClick={(e) => {
          e.preventDefault();
          onSubmitCallback(true);
        }}
        width={isPromoCodeApplied && promoCodeData ? "100%" : ""}
        btnPadding={
          isPromoCodeApplied && promoCodeData ? "11px 20px" : "15px 32px"
        }
        bgColor={theme.colorsV2.shyft.purple_text}
        hoverColor={theme.colorsV2.shyft.purple_text}
        variant="primary1"
        fontFamily={"var(--man-700)"}
        textAlign="center"
        fontSize={["14px", "15px", "16px"]}
        version={"v2"}
        isLoading={isLoading}
        minWidth={"123px"}
        minHeight={"56px"}
        boxShadow={`0px 10px 20px rgba(${theme.colors.shadows.purple_3}, 0.18), 0px 2px 6px rgba(${theme.colors.shadows.purple_3}, 0.12), 0px 0px 1px rgba(${theme.colors.shadows.black}, 0.04)`}
      >
        {primaryCTAData.primaryText}
        {primaryCTAData.strikeThroughText && (
          <StrikeThroughText>
            {primaryCTAData.strikeThroughText}
          </StrikeThroughText>
        )}
        {primaryCTAData.leadingText && primaryCTAData.leadingText}
        {isLoading && (
          <>
            &nbsp; <Loader />
          </>
        )}
      </Button>
    </FlexDiv>
  );

  const SecondaryCTA = (
    <FlexDiv alignItems="center" mt="16px" flexDirection="column">
      <Typography
        variant="span"
        fontFamily="var(--font-700)"
        color={theme.colors.quote}
        mb="16px"
        textAlign="center"
      >
        OR
      </Typography>
      <Button
        hoverColor={`${theme.colors.quote}2e`}
        bgColor={`${theme.colors.quote}2e`}
        variant="primary1"
        fontFamily="var(--font-700)"
        TextColor={theme.colorsV2.shyft.purple}
        onClick={(e) => {
          e.preventDefault();
          onSubmitCallback(true);
        }}
        btnPadding="11px 20px"
        textAlign="center"
        fontSize="15px"
      >
        {secondaryCTA}
      </Button>
    </FlexDiv>
  );

  const ClassInfo = (
    <SubHeading
      mt="10px"
      variant="paragraph2"
      textAlign="center"
      fontSize="10px"
    >
      {classInfo}
    </SubHeading>
  );

  const NeedMoreInfo = (
    <FlexDiv justifyContent="center" textAlign="center">
      <BoldText
        TextColor={theme.colors.gray_50}
        variant="link"
        onClick={(e) => {
          e.preventDefault();
          onSubmitCallback(true);
        }}
        fontFamily="var(--font-700)"
      >
        {needMoreInfo}
      </BoldText>
    </FlexDiv>
  );

  return (
    <BodyWrap className="callback-form-containers" width="100%">
      <FlexDiv flexDirection="column" width="100%">
        <FormBox onSubmit={(e) => e.preventDefault()}>
          {showGoalField && GoalField}
          {showSelectPackage && ServiceField}
          {showNameField && NameField}
          {showCVIDField && CVIDField}
          {showPhoneField && PhoneField}
          {showEmailField && EmailField}
          {showCompanyNameField && CompanyNameField}
          {showPromoCodeField && PromoCodeField}
          {showReferralField && ReferralField}
          {PrimaryCTA}
          {showSecondaryCTA && SecondaryCTA}
          {classInfo && ClassInfo}
          {needMoreInfo && NeedMoreInfo}
        </FormBox>
      </FlexDiv>
    </BodyWrap>
  );
};

export default FormInputs;
